import { ArrowRight } from "lucide-react"
import figmaLogo from "../assets/figma.webp"
import logo from "../assets/logo_h.svg"
import riotLogo from "../assets/riot.webp"
import scaleLogo from "../assets/sca;e.webp"
import slbLogo from "../assets/slb.webp"
import spluckLogo from "../assets/spluck.webp"
import travleLogo from "../assets/travle.webp"
import webflowLogo from "../assets/webflow.webp"
import { useNavigate } from "react-router-dom"

const pageStyles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');
  .demo-page { font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif; }

  @keyframes marquee {
    0%   { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }
  .marquee-track {
    display: flex;
    width: max-content;
    animation: marquee 14s linear infinite;
  }
  .marquee-track:hover { animation-play-state: paused; }

  .interview-card {
    transition: box-shadow 0.25s ease, transform 0.25s ease;
  }
  .interview-card:hover {
    box-shadow: 0 12px 32px rgba(0,0,0,0.10);
    transform: translateY(-3px);
  }
  .interview-card:hover .try-btn {
    background: #111827;
  }
  .try-btn {
    transition: background 0.2s ease;
  }
`

const logos = [
  { src: riotLogo, alt: "Riot Games" },
  { src: scaleLogo, alt: "Scale" },
  { src: slbLogo, alt: "SLB" },
  { src: spluckLogo, alt: "Splunk" },
  { src: travleLogo, alt: "Travelers" },
  { src: figmaLogo, alt: "Figma" },
  { src: webflowLogo, alt: "Webflow" },
]

// Gradient tints pulled directly from HomePage bento cards
const cardGradients = [
  "linear-gradient(135deg, #f3a67520, #e9d7ff 90%)",
  "linear-gradient(135deg, #a8edea20, #fed6e3 90%)",
  "linear-gradient(135deg, #ffecd220, #fcb69f 90%)",
  "linear-gradient(135deg, #d299c220, #fef9d7 90%)",
  "linear-gradient(135deg, #89f7fe20, #66a6ff 90%)",
  "linear-gradient(135deg, #f3a67520, #e9d7ff 90%)",
]

const avatarColors = [
  "linear-gradient(135deg, #667eea, #764ba2)",
  "linear-gradient(135deg, #f093fb, #f5576c)",
  "linear-gradient(135deg, #4facfe, #00f2fe)",
  "linear-gradient(135deg, #43e97b, #38f9d7)",
  "linear-gradient(135deg, #fa709a, #fee140)",
  "linear-gradient(135deg, #a18cd1, #fbc2eb)",
]

const interviewers = [
  {
    id: 1,
    name: "Alex Chen",
    role: "AI Engineer",
    tags: ["LLM", "Transformers", "Similarity Search"],
    company: "Stripe",
    duration: "20 min",
    type: "Coding",
    isNew: true,
    color: avatarColors[0],
    gradient: cardGradients[0],
    meetingId: "meeting_mmotax8y",
  },
  {
    id: 2,
    name: "Aria Nguyen",
    role: "Frontend Engineer",
    tags: ["React", "State Management", "Design"],
    company: "Cohere",
    duration: "20 min",
    type: "Coding",
    isNew: true,
    color: avatarColors[5],
    gradient: cardGradients[5],
    meetingId: "meeting_mmow3bzr",
  },
  {
    id: 3,
    name: "Ranjan Sharma",
    role: "Backend Engineer",
    tags: ["DSA", "Time Complexity", "Edge Case Analysis"],
    company: "Atlassian",
    duration: "20 min",
    type: "System Design",
    isNew: true,
    color: avatarColors[1],
    gradient: cardGradients[1],
    meetingId: "meeting_mmpybwdb",
  },
  {
    id: 4,
    name: "Marcus Lee",
    role: "Product Manager",
    tags: ["Strategy", "Analytics"],
    company: "Notion",
    duration: "20 min",
    type: "Case Study",
    isNew: true,
    color: avatarColors[2],
    gradient: cardGradients[2],
    meetingId: "meeting_mmq1m9rx",
  },
  {
    id: 5,
    name: "Sofia Patel",
    role: "VC Associate",
    tags: ["Analysis"],
    company: "HashiCorp",
    duration: "20 min",
    type: "Conversation",
    isNew: false,
    color: avatarColors[3],
    gradient: cardGradients[3],
    meetingId: "meeting_mmq2z0j1",
  },
  {
    id: 6,
    name: "James Wright",
    role: "Data Scientist",
    tags: ["Analysis", "Python"],
    company: "Linear",
    duration: "20 min",
    type: "Coding",
    isNew: false,
    color: avatarColors[4],
    gradient: cardGradients[4],
    meetingId: "meeting_mmq6r1k2",
  },
]

const DemoAiInterviewPage = () => {
  const navigate = useNavigate()

  return (
    <div className="demo-page flex flex-col min-h-screen bg-white">
      <style>{pageStyles}</style>

      {/* ── Header ── */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-3.5 flex items-center justify-between">
          <div
            className="flex items-center cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => (window.location.href = "/")}
          >
            <img
              src={logo}
              alt="Resolut Logo"
              className="w-6 h-6 drop-shadow-md"
            />
            <span className="ml-2 text-xl font-semibold font-satoshi tracking-tight text-gray-800">
              Resolut
            </span>
          </div>
          <button
            onClick={() =>
              window.open(
                "https://calendly.com/akshay-kumar3389/new-meeting?month=2026-01",
                "_blank",
              )
            }
            className="px-5 py-2.5 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors"
          >
            Book a Demo
          </button>
        </div>
      </header>

      {/* ── Hero ── */}
      <div className="bg-white py-14 text-center px-6 border-b border-gray-100">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
          AI Hiring Platform
        </p>
        <h1 className="font-satoshi text-4xl md:text-5xl font-[300] tracking-tight text-gray-900">
          Try Our Superhuman AI Interview Agents
        </h1>
      </div>

      {/* ── Scrolling Logos ── */}
      <div className="bg-white border-b border-gray-100 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center" style={{ height: "80px" }}>
            <p className="hidden sm:block flex-shrink-0 text-gray-500 text-sm font-medium pr-6 whitespace-nowrap">
              Trusted by global teams
            </p>
            <div className="hidden sm:block w-px h-6 bg-gray-200 mr-6 flex-shrink-0" />
            <div className="relative flex-1 overflow-hidden h-full">
              <div
                className="absolute left-0 top-0 bottom-0 w-12 z-10 pointer-events-none"
                style={{
                  background: "linear-gradient(to right, #ffffff, transparent)",
                }}
              />
              <div
                className="absolute right-0 top-0 bottom-0 w-12 z-10 pointer-events-none"
                style={{
                  background: "linear-gradient(to left, #ffffff, transparent)",
                }}
              />
              <div className="flex items-center h-full">
                <div className="marquee-track">
                  {[...logos, ...logos].map((l, i) => (
                    <div
                      key={i}
                      className="flex-shrink-0 flex items-center justify-center mx-6 h-8"
                    >
                      <img
                        src={l.src}
                        alt={l.alt}
                        className="max-h-8 w-auto object-contain opacity-60 hover:opacity-90 transition-opacity"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Cards ── */}
      <main className="flex-1 py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {interviewers.map((interviewer) => {
              const initials = interviewer.name
                .split(" ")
                .map((n) => n[0])
                .join("")
              return (
                <div
                  key={interviewer.id}
                  className="interview-card rounded-2xl overflow-hidden border border-gray-100 shadow-sm flex flex-col"
                >
                  {/* ── Top gradient area ── */}
                  <div
                    className="relative px-6 pt-8 pb-6 flex flex-col items-start"
                    style={{ background: interviewer.gradient }}
                  >
                    {/* NEW + Type row */}
                    <div className="flex items-center justify-between w-full mb-5">
                      <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest">
                        {interviewer.type}
                      </span>
                      {interviewer.isNew && (
                        <span className="bg-pink-500 text-white text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                          New
                        </span>
                      )}
                    </div>

                    {/* Avatar + Identity */}
                    <div className="flex items-center gap-4 w-full">
                      <div
                        className="w-12 h-12 rounded-full flex-shrink-0 flex items-center justify-center text-white text-base font-semibold shadow-md"
                        style={{ background: interviewer.color }}
                      >
                        {initials}
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-gray-900 leading-tight">
                          {interviewer.name}
                        </h3>
                        <p className="text-sm text-gray-500 mt-0.5">
                          {interviewer.role}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {interviewer.company}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* ── Bottom white area ── */}
                  <div className="bg-white px-6 py-5 flex flex-col gap-4 flex-1">
                    {/* Tags */}
                    <div className="flex flex-wrap gap-1.5">
                      {interviewer.tags.map((tag, i) => (
                        <span
                          key={i}
                          className="text-xs text-gray-600 bg-gray-50 border border-gray-200 px-2.5 py-1 rounded-lg font-medium"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    {/* Divider */}
                    <div className="border-t border-gray-100" />

                    {/* Footer row */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400 font-medium">
                        {interviewer.duration} · AI-powered
                      </span>
                      <button
                        className="try-btn flex items-center gap-1.5 px-4 py-2 bg-gray-900 text-white rounded-lg text-xs font-semibold"
                        onClick={() =>
                          navigate(`/ai-interview/${interviewer.meetingId}`)
                        }
                      >
                        Try Interview
                        <ArrowRight size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </main>
    </div>
  )
}

export default DemoAiInterviewPage
